import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { SearchResponseWithMeta } from "../api/search";
import { getSearchResults } from "../api/search";
import { useEffect, useRef } from "react";
import { clientEventSystem, CLIENT_EVENTS } from "../events/clientEvents";
import { useStatusStore } from "../stores/useStatusStore";

// Create stable event handlers outside the hook to ensure they persist
// and don't get recreated with each render
const setupGlobalEventHandlers = (() => {
  let isSetup = false;

  return (queryClient: ReturnType<typeof useQueryClient>) => {
    if (isSetup) return;

    const handleClientArchived = (_clientId: string) => {
      // Invalidate all search queries to refetch with new archived status
      queryClient.invalidateQueries({
        queryKey: ["search"],
        refetchType: "all",
      });
      queryClient.refetchQueries({ queryKey: ["search"], type: "active" });
    };

    const handleClientStatusChanged = () => {
      // For status changes, invalidate all search queries
      queryClient.invalidateQueries({
        queryKey: ["search"],
        refetchType: "all",
      });
      queryClient.refetchQueries({ queryKey: ["search"], type: "active" });
    };

    // Subscribe to client events at the global level
    clientEventSystem.subscribe(
      CLIENT_EVENTS.CLIENT_ARCHIVED,
      handleClientArchived
    );
    clientEventSystem.subscribe(
      CLIENT_EVENTS.CLIENT_STATUS_CHANGED,
      handleClientStatusChanged
    );

    isSetup = true;
  };
})();

export const useSearch = () => {
  const selectedTaxYear = useStatusStore((state) => state.selectedTaxYear);
  const queryClient = useQueryClient();
  const isSubscribed = useRef(false);
  const prevTaxYear = useRef(selectedTaxYear);

  // Set up global event handlers once
  useEffect(() => {
    if (!isSubscribed.current) {
      setupGlobalEventHandlers(queryClient);
      isSubscribed.current = true;
    }
  }, [queryClient]);

  // Invalidate and refetch when tax year changes
  useEffect(() => {
    if (prevTaxYear.current !== selectedTaxYear) {
      prevTaxYear.current = selectedTaxYear;
      queryClient.invalidateQueries({ queryKey: ["search"] });
    }
  }, [selectedTaxYear, queryClient]);

  const {
    data,
    isLoading: isLoadingClients,
    isError: isErrorClients,
    error: errorClients,
  } = useQuery<SearchResponseWithMeta, Error>({
    queryKey: ["search", selectedTaxYear],
    queryFn: async () => {
      const params: any = {};
      if (selectedTaxYear !== null) {
        params.tax_year = selectedTaxYear;
      }
      return await getSearchResults(params);
    },
    staleTime: 0, // Don't use stale data - always refetch
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  return {
    clients: data?.results,
    availableTaxYears: data?.availableTaxYears || [],
    isLoadingClients,
    isErrorClients,
    errorClients,
  };
};
